import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, message } from 'antd';
import { EditStaffingRequestForm, FormValuesType } from '@jobs/components';
import { useQuery, useMutation } from '@common/hooks';
import {
  ButtonGroup,
  Drawer,
  ErrorHandler,
  LoadingState,
  Modal,
} from '@common/components';
import {
  STAFFING_REQUEST_QUERY,
  UPDATE_STAFFING_REQUEST_MUTATION,
  CertificationCustomType,
  ClinicalSpecialtyCustomType,
} from '@jobs/graphql';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { hasFormAnyErrors } from '@common/services';

export function EditStaffingRequestDrawer({
  open,
  onClose,
  staffingRequestId,
}: {
  open: boolean;
  onClose: () => void;
  staffingRequestId?: string;
}) {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [increasedEstimatedCost, setIncreasedEstimatedCost] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  const { error, data } = useQuery(STAFFING_REQUEST_QUERY, {
    skip: !staffingRequestId,
    variables: {
      id: staffingRequestId!,
    },
  });

  const [updateStaffingRequest, { loading: loadingMutation }] = useMutation(
    UPDATE_STAFFING_REQUEST_MUTATION,
    {
      onCompleted: (mutationData) => {
        if (mutationData?.updateStaffingRequest?.staffingRequest) {
          message.success('Staffing Request has been saved.');
          onFormClose();
        }
        if (mutationData?.updateStaffingRequest?.errors) {
          addErrorsToForm(form, mutationData.updateStaffingRequest.errors);
        }
      },
      onError: () => message.error('Error occurred, try again later'),
      refetchQueries: [
        'StaffingRequestApplicationData',
        'StaffingRequestPageQuery',
      ],
    }
  );

  const onSubmit = (
    values: FormValuesType,
    deviated: boolean,
    selectedSpecialties: ClinicalSpecialtyCustomType[],
    selectedCertifications: CertificationCustomType[]
  ) => {
    const approvalChainTemplate = values.approvalChainTemplateSource;
    delete values.approvalChainTemplateSource;
    delete values.certifications;
    delete values.specialtyId;

    updateStaffingRequest({
      variables: {
        staffingRequestId: staffingRequestId,
        approvalChainTemplateSource: approvalChainTemplate,
        deviated: deviated,
        input: {
          ...values,
          specialties: selectedSpecialties.map(
            (specialty: ClinicalSpecialtyCustomType) => ({
              clinicalSpecialtyId: specialty.id,
              required: specialty.required || false,
            })
          ),
          certifications: selectedCertifications.map(
            (cert: CertificationCustomType) => ({
              certificationId: cert.id,
              required: cert.required || false,
            })
          ),
        },
      },
    });
  };

  const onFormClose = () => {
    setCurrentStep(1);
    setIncreasedEstimatedCost(false);
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (
      open &&
      data?.staffingRequest?.canUpdateRateAffectedDetails?.value &&
      data?.staffingRequest?.activeApplicationsWithoutInactiveOffers
    ) {
      confirmActiveApplicationsModal(onClose);
    }
  }, [data, onClose, open]);

  const hasStepThree = useMemo(() => {
    const hasEditApprovals =
      (data?.staffingRequest?.organizationUnit?.approvalChainTemplates?.filter(
        (a) => a.actionType === 'staffing_request_open'
      ).length ?? 0) > 0;

    return hasEditApprovals && increasedEstimatedCost;
  }, [data, increasedEstimatedCost]);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        messageType="contextual"
        log="EditStaffingRequestDrawer"
      />
    );
  }

  return (
    <>
      <Drawer
        title="Edit Staffing Request"
        open={open}
        onClose={onFormClose}
        footer={
          <ButtonGroup>
            <Button
              key="cancel"
              size="large"
              onClick={() => {
                if (currentStep === 1) {
                  onClose();
                } else if (currentStep === 2) {
                  setCurrentStep(1);
                } else {
                  setCurrentStep(2);
                }
              }}
            >
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              key="action"
              type="primary"
              size="large"
              onClick={() => {
                if (currentStep === 1) {
                  setCurrentStep(2);
                } else if (currentStep === 2 && hasStepThree) {
                  setCurrentStep(3);
                } else {
                  confirmChangesModal(form.submit);
                }
              }}
              loading={loadingMutation}
              disabled={hasErrors}
            >
              {(hasStepThree && currentStep === 2) || currentStep === 1
                ? 'Continue'
                : 'Save Edits'}
            </Button>
          </ButtonGroup>
        }
      >
        {data?.staffingRequest ? (
          <EditStaffingRequestForm
            form={form}
            onSubmit={onSubmit}
            certifications={data.certificationList}
            clinicalSpecialties={data.clinicalSpecialties}
            technologies={data.technologies}
            staffingRequest={data.staffingRequest}
            partOneVisible={currentStep === 1}
            partTwoVisible={currentStep === 2}
            partThreeVisible={currentStep === 3}
            setIncreasedEstimatedCost={setIncreasedEstimatedCost}
            onValuesChange={() => hasFormAnyErrors(form, setHasErrors)}
          />
        ) : (
          <LoadingState />
        )}
      </Drawer>
    </>
  );
}

function confirmActiveApplicationsModal(onCancel: () => void) {
  Modal.confirm({
    title: 'Do you want to continue editing?',
    content:
      'There are applications that are submitted or in review. (Sent or accepted offers will not be affected).',
    onCancel: onCancel,
  });
}

function confirmChangesModal(onConfirm: () => void) {
  Modal.confirm({
    title: 'Confirm Changes on the Staffing Request?',
    content:
      'Once this has been approved (if applicable), a notification will be sent to the supplier(s) if you updated start date, length, shift details, rate, open positions, or the description.',
    onOk: onConfirm,
  });
}
